/**
 * SF-156: Race/Rank/Personality flavor-chip metadata for the Dossier mobile roster row and detail
 * view. Pure `{icon, color, label}` lookups only — no JSX (a `.ts` file can't export JSX-returning
 * components under this project's TypeScript config; the presentational half is
 * `DossierFlavorChip.tsx`).
 *
 * All three categories are real, pre-existing game data — only the icon/color per value is a
 * cosmetic choice made for this spec (see design/DESIGN_SOURCE.md's "Flavor chips" section and its
 * rejected-idea note on not inventing a classification). Race/Rank are always-public roster fields;
 * Personality is intel-gated (Crystal Ball) — the gating itself is the caller's responsibility
 * (`latestCb != null`), not this module's.
 */

import { COLORS } from '../constants/theme';
import { formatEnumLabel } from './format';
import { getPersonalityDisplayName } from '../constants/game';

export interface ChipMeta {
  icon: string;
  color: string;
  label: string;
}

interface IconColor {
  icon: string;
  color: string;
}

const FALLBACK_META: IconColor = { icon: '❔', color: COLORS.text.tertiary };

/**
 * Race — matches `constants/game.ts`'s `RACES` (8 values). Icon/color is cosmetic; the category
 * itself is real, public roster data (`DossierProvinceRow.race`).
 */
const RACE_META: Record<string, IconColor> = {
  HUMAN:    { icon: '⚔️', color: '#c9a876' },
  ELF:      { icon: '🏹', color: '#7ec9a8' },
  DWARF:    { icon: '⛏️', color: '#c78a4a' },
  ORC:      { icon: '🪓', color: '#c25a5a' },
  HALFLING: { icon: '🌾', color: '#8fbf6a' },
  UNDEAD:   { icon: '💀', color: '#9a8fbf' },
  AVIAN:    { icon: '🦅', color: '#8ac9e0' },
  FAERY:    { icon: '✨', color: '#c79ee0' },
};

/**
 * Rank/title — the same `PEASANT...PRINCE` progression `KingdomRosterTable.tsx`'s `RANK_ORDER`
 * already encodes (9 values). Icon/color is cosmetic; the category and its ordering are real,
 * public roster data (`DossierProvinceRow.rank`).
 */
const RANK_META: Record<string, IconColor> = {
  PEASANT:  { icon: '🌾', color: COLORS.text.muted },
  KNIGHT:   { icon: '🗡️', color: '#9aa4bd' },
  LORD:     { icon: '🏵️', color: '#c78a4a' },
  BARON:    { icon: '🏰', color: '#c9a876' },
  VISCOUNT: { icon: '🦉', color: '#8ac9e0' },
  COUNT:    { icon: '🛡️', color: COLORS.link },
  MARQUIS:  { icon: '⚜️', color: '#c79ee0' },
  DUKE:     { icon: '🏆', color: COLORS.gold },
  PRINCE:   { icon: '👑', color: COLORS.warning },
};

/**
 * Personality — the real `PersonalityId` game concept (`shared/id/PersonalityId.java`), a
 * player-chosen province trait with real gameplay modifiers (matches `constants/game.ts`'s
 * `PERSONALITIES`, 9 values incl. legacy `SHEPHERD`). Icon/color is cosmetic; the category is real
 * and intel-gated (`CrystalBallData.personality`) — the caller only invokes this once Crystal Ball
 * is on file for the member.
 */
const PERSONALITY_META: Record<string, IconColor> = {
  MERCHANT:  { icon: '💰', color: COLORS.gold },
  SAGE:      { icon: '📖', color: COLORS.link },
  ROGUE:     { icon: '🎭', color: '#b0708a' },
  MYSTIC:    { icon: '🔮', color: '#b89fe0' },
  WARRIOR:   { icon: '🔥', color: '#e0575a' },
  TACTICIAN: { icon: '🎯', color: '#8ac9e0' },
  CLERIC:    { icon: '🕊️', color: '#c8d4e0' },
  WAR_HERO:  { icon: '🎖️', color: COLORS.warning },
  SHEPHERD:  { icon: '🐑', color: '#8fbf6a' },
};

/** Race chip metadata. Unknown values fall back to a neutral icon/color, never crash. */
export function raceChip(race: string): ChipMeta {
  const meta = RACE_META[race.toUpperCase()] ?? FALLBACK_META;
  return { ...meta, label: formatEnumLabel(race) };
}

/** Rank chip metadata. Unknown values fall back to a neutral icon/color, never crash. */
export function rankChip(rank: string): ChipMeta {
  const meta = RANK_META[rank.toUpperCase()] ?? FALLBACK_META;
  return { ...meta, label: formatEnumLabel(rank) };
}

/**
 * Personality chip metadata. Caller gates on Crystal Ball presence — this function only builds
 * metadata for a given value. Unknown values fall back to a neutral icon/color; per existing
 * pinned behavior (`game.test.ts`), `getPersonalityDisplayName` returns the raw string verbatim
 * for an unmapped value rather than a translated "Unknown" label.
 */
export function personalityChip(personality: string): ChipMeta {
  const meta = PERSONALITY_META[personality.toUpperCase()] ?? FALLBACK_META;
  return { ...meta, label: getPersonalityDisplayName(personality) };
}
